import React from "react";
import {
  Card,
  CardContent,
  Box,
  Typography,
  LinearProgress,
  Avatar,
  Chip,
  Button,
  useTheme,
  Skeleton,
} from "@mui/material";
import { AccountBalance as AccountBalanceIcon } from "@mui/icons-material";
import { Goal, GoalLinkedAccount } from "../../lib/types";
import { GOAL_ICONS } from "./GoalForm";

interface GoalCardProps {
  goal: Goal;
  accounts?: GoalLinkedAccount[];
  accountsLoading?: boolean;
  onEdit: (goal: Goal) => void;
  onLinkAccounts: (goal: Goal) => void;
}

const normalizeGoalCategories = (goal: Goal): string[] => {
  const raw = Array.isArray(goal.categories) ? goal.categories : [];
  const cleaned = raw.map((c) => String(c || "").trim()).filter(Boolean);
  // Dedupe case-insensitively while preserving order.
  const seen = new Set<string>();
  const result: string[] = [];
  for (const c of cleaned) {
    const key = c.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(c);
  }
  return result;
};

const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  accounts = [],
  accountsLoading = false,
  onEdit,
  onLinkAccounts,
}) => {
  const theme = useTheme();

  // Calculate progress percentage
  const progress = Math.min(
    (goal.currentAmount / goal.targetAmount) * 100,
    100
  );

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate days remaining
  const getDaysRemaining = () => {
    const targetDate = new Date(goal.targetDate);
    const today = new Date();
    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining();
  const iconConfig = GOAL_ICONS[goal.icon] || GOAL_ICONS.other;
  const categories = normalizeGoalCategories(goal);

  // Get status color and label
  const getStatusConfig = () => {
    switch (goal.status) {
      case "completed":
        return { color: "success" as const, label: "Completed" };
      case "paused":
        return { color: "warning" as const, label: "Paused" };
      default:
        return { color: "primary" as const, label: "Active" };
    }
  };

  const statusConfig = getStatusConfig();

  // Get progress bar color based on progress
  const getProgressColor = () => {
    if (progress >= 100) return "success";
    if (progress >= 75) return "primary";
    if (progress >= 50) return "info";
    if (progress >= 25) return "warning";
    return "error";
  };

  const handleLinkAccountsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onLinkAccounts(goal);
  };

  const linkedAccountsLoading =
    accountsLoading &&
    Boolean(goal.linkedAccountIds && goal.linkedAccountIds.length > 0);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        opacity: goal.status === "paused" ? 0.7 : 1,
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[4],
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box
        onClick={() => onEdit(goal)}
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          cursor: "pointer",
        }}
      >
        <CardContent sx={{ flexGrow: 1, width: "100%" }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: iconConfig.color,
                }}
              >
                {React.cloneElement(
                  iconConfig.icon as React.ReactElement<{
                    style?: React.CSSProperties;
                  }>,
                  { style: { fontSize: 28 } }
                )}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 600 }}
                  >
                    {goal.name}
                  </Typography>
                  {goal.status !== "active" && (
                    <Chip
                      label={statusConfig.label}
                      size="small"
                      color={statusConfig.color}
                    />
                  )}
                </Box>
                {goal.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {goal.description}
                  </Typography>
                )}
                {categories.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                  >
                    {categories.map((c) => (
                      <Chip
                        key={c}
                        label={c}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Progress Section */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                mb: 1,
              }}
            >
              {linkedAccountsLoading ? (
                <>
                  <Skeleton width={120} height={32} />
                  <Skeleton width={80} height={20} />
                </>
              ) : (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {formatCurrency(goal.currentAmount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {formatCurrency(goal.targetAmount)}
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ position: "relative" }}>
              {linkedAccountsLoading ? (
                <Skeleton variant="rectangular" height={20} />
              ) : (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    color={getProgressColor()}
                    sx={{
                      height: 20,
                      borderRadius: 10,
                      backgroundColor: theme.palette.grey[200],
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 10,
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      fontWeight: 600,
                      color:
                        progress > 50 ? "#fff" : theme.palette.text.primary,
                      textShadow:
                        progress > 50 ? "0 1px 2px rgba(0,0,0,0.3)" : "none",
                    }}
                  >
                    {progress.toFixed(0)}%
                  </Typography>
                </>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 0.5,
              }}
            >
              {linkedAccountsLoading ? (
                <Skeleton width={110} height={18} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {formatCurrency(goal.targetAmount - goal.currentAmount)} to go
                </Typography>
              )}
            </Box>
          </Box>

          {/* Target Date */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Target Date
            </Typography>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {new Date(goal.targetDate).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  timeZone: "UTC",
                })}
              </Typography>
              <Typography
                variant="caption"
                color={
                  daysRemaining < 0
                    ? "error.main"
                    : daysRemaining <= 30
                      ? "warning.main"
                      : "text.secondary"
                }
              >
                {daysRemaining < 0
                  ? `${Math.abs(daysRemaining)} days overdue`
                  : daysRemaining === 0
                    ? "Due today"
                    : daysRemaining === 1
                      ? "1 day left"
                      : `${daysRemaining} days left`}
              </Typography>
            </Box>
          </Box>

          {/* Linked Accounts Section */}
          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {goal.linkedAccountIds && goal.linkedAccountIds.length > 0 ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Linked Accounts
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
                    onClick={handleLinkAccountsClick}
                    sx={{ fontSize: "0.75rem", py: 0 }}
                  >
                    Manage
                  </Button>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {linkedAccountsLoading
                    ? goal.linkedAccountIds.map((accountId) => (
                        <Skeleton
                          key={accountId}
                          variant="rounded"
                          width={110}
                          height={24}
                        />
                      ))
                    : goal.linkedAccountIds.map((accountId) => {
                        const account = accounts.find(
                          (a) => a.id === accountId
                        );
                        return (
                          <Chip
                            key={accountId}
                            label={account?.name || "Unknown Account"}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        );
                      })}
                </Box>
              </Box>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<AccountBalanceIcon />}
                onClick={handleLinkAccountsClick}
                sx={{
                  borderStyle: "dashed",
                  color: "text.secondary",
                  borderColor: theme.palette.divider,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    borderStyle: "dashed",
                  },
                }}
              >
                Link Account to Track Progress
              </Button>
            )}
          </Box>
        </CardContent>
      </Box>
    </Card>
  );
};

export default GoalCard;
